import { useEffect, useState } from 'react';
import { useBrand } from './BrandProvider.js';
import { splitBrandName } from './util/text.js';

/**
 * Brand-name wordmark. On first paint (before `/api/brand` resolves) it renders the neutral core
 * "Alere", so neither portal flashes the wrong brand. Once the brand loads, the distinguishing
 * letters fade in around the core — "AR…" for Aralere, "…IT" for AlereIT. Any brand whose name
 * doesn't contain the "alere" core just renders its plain name.
 *
 * Segmentation comes from `splitBrandName` (util/text), shared with the plain-text
 * `brandDisplayName`, so the styled and text renderings of a brand never drift.
 */
export function BrandWordmark({ className }: { className?: string }): JSX.Element {
  const { brand } = useBrand();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!brand) return;
    const t = setTimeout(() => setRevealed(true), 30);
    return () => clearTimeout(t);
  }, [brand]);

  const name = brand?.name ?? null;
  if (!name) return <span className={className}>Alere</span>;

  const { prefix, core, suffix } = splitBrandName(name);

  const affix =
    `text-[var(--color-accent)] transition-all duration-500 ease-out ${revealed ? 'opacity-100' : 'opacity-0'}`;

  return (
    <span className={className}>
      {prefix ? <span className={affix}>{prefix.toUpperCase()}</span> : null}
      {core}
      {suffix ? <span className={affix}>{suffix.toUpperCase()}</span> : null}
    </span>
  );
}
